import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import photo1 from '../photo/1.png';
import photo2 from '../photo/2.png';
import photo3 from '../photo/3.png';
import photo4 from '../photo/4.png';
import photo5 from '../photo/5.png';
import photo6 from '../photo/6.png';

const BackButton = styled(motion.button)`
  position: fixed;
  top: 30px;
  left: 30px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 9999;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    padding: 8px 16px;
    font-size: 14px;
    z-index: 9999;
  }
  
  &:hover {
    background: linear-gradient(135deg, #5a6fd8, #6a4c93);
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(102, 126, 234, 0.6);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(20px);
  border-radius: 30px;
  margin-top: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.1) 0%,
      rgba(118, 75, 162, 0.05) 50%,
      rgba(240, 147, 251, 0.1) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
  
  > * {
    position: relative;
    z-index: 2;
  }
`;

const FeatureRow = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: 8rem;
  gap: 4rem;
  
  @media (max-width: 968px) {
    flex-direction: column;
    margin-bottom: 6rem;
    gap: 2rem;
  }
  
  &.reverse {
    flex-direction: row-reverse;
    
    @media (max-width: 968px) {
      flex-direction: column;
    }
  }
`;

const FeatureImage = styled(motion.div)`
  flex: 0 0 500px;
  position: relative;
  
  img {
    width: 100%;
    height: auto;
    border-radius: 25px;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
    transition: all 0.4s ease;
    border: 2px solid rgba(255, 255, 255, 0.1);
    
    &:hover {
      transform: translateY(-10px) scale(1.02);
      box-shadow: 0 35px 70px rgba(102, 126, 234, 0.4);
      border-color: rgba(102, 126, 234, 0.3);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    background: linear-gradient(45deg, #667eea, #764ba2, #f093fb, #f5576c);
    border-radius: 30px;
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: -1;
    filter: blur(20px);
  }
  
  &:hover::before {
    opacity: 0.3;
  }
  
  @media (max-width: 968px) {
    flex: none;
    max-width: 450px;
  }
  
  @media (max-width: 480px) {
    max-width: 350px;
  }
`;

const FeatureContent = styled(motion.div)`
  flex: 1;
  
  h3 {
    font-size: 2.5rem;
    font-weight: 800;
    background: linear-gradient(45deg, #667eea, #764ba2, #f093fb);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    text-shadow: 0 0 30px rgba(102, 126, 234, 0.3);
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 2rem;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
  
  .feature-highlight {
    background: linear-gradient(135deg, #ffd700, #ffed4e, #fff59d);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 900;
    text-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
  }
`;

const Divider = styled.div`
  width: 150px;
  height: 4px;
  background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
  margin: 6rem auto;
  border-radius: 10px;
  box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
    border-radius: 12px;
    opacity: 0.3;
    filter: blur(8px);
    z-index: -1;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 900;
  text-align: center;
  margin-bottom: 4rem;
  background: linear-gradient(45deg, #667eea, #764ba2, #f093fb, #f5576c);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 40px rgba(102, 126, 234, 0.4);
  
  .gradient-text {
    background: linear-gradient(135deg, #ffd700, #ffed4e, #fff59d);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-top: 60px;
  }
`;

export const Documentation = ({ onBack }) => {
  return (
    <div>
      <BackButton
        onClick={onBack}
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        ← Ana Sayfa
      </BackButton>

      {/* Features Section */}
      <FeaturesSection style={{ paddingTop: '8rem' }}>
        <SectionTitle
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="gradient-text">Uygulama Özellikleri</span>
        </SectionTitle>

        {/* Feature 1 - Sol */}
        <FeatureRow
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <FeatureImage
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <img src={photo1} alt="Özellik 1" />
          </FeatureImage>
          <FeatureContent
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3>Güvenli <span className="feature-highlight">Giriş</span></h3>
            <p>
              LOGO Tiger ERP sisteminizle tam entegre güvenli giriş sistemi. 
              Mevcut kullanıcı bilgilerinizle kolayca giriş yapın ve verilerinize anında erişim sağlayın.
            </p>
          </FeatureContent>
        </FeatureRow>

        <Divider />

        {/* Feature 2 - Sağ */}
        <FeatureRow 
          className="reverse"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <FeatureImage
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <img src={photo2} alt="Özellik 2" />
          </FeatureImage>
          <FeatureContent
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3><span className="feature-highlight">Dashboard</span> Görünümü</h3>
            <p>
              İş süreçlerinizin özetini tek bakışta görün. KPI'larınız, grafikleriniz 
              ve önemli bilgileriniz modern bir arayüzle karşınızda.
            </p>
          </FeatureContent>
        </FeatureRow>

        <Divider />

        {/* Feature 3 - Sol */}
        <FeatureRow
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <FeatureImage
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <img src={photo3} alt="Özellik 3" />
          </FeatureImage>
          <FeatureContent
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3>Detaylı <span className="feature-highlight">Raporlar</span></h3>
            <p>
              Satış, finans, stok ve operasyon raporlarınızı mobil cihazınızdan detaylı 
              bir şekilde görüntüleyin ve analiz edin. İhtiyacınıza göre raporlarınızı sizler için ekleyelim.
            </p>
          </FeatureContent>
        </FeatureRow>

        <Divider />

        {/* Feature 4 - Sağ */}
        <FeatureRow 
          className="reverse"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <FeatureImage
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <img src={photo4} alt="Özellik 4" />
          </FeatureImage>
          <FeatureContent
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3><span className="feature-highlight">Anlık</span> Bildirimler</h3>
            <p>
              Önemli iş olayları hakkında anında bildirim alın. Kritik durumları 
              kaçırmayın ve hızlı aksiyon alın.
            </p>
          </FeatureContent>
        </FeatureRow>

        <Divider />

        {/* Feature 5 - Sol */}
        <FeatureRow
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <FeatureImage
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <img src={photo5} alt="Özellik 5" />
          </FeatureImage>
          <FeatureContent
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3><span className="feature-highlight">Yapay Zeka</span> Desteği</h3>
            <p>
              Raporlarınızı ChatGPT desteği ile yorumlayın. Yapay zeka teknolojisi 
              ile verilerinizi analiz edin ve akıllı öneriler alın.
            </p>
          </FeatureContent>
        </FeatureRow>

        <Divider />

        {/* Feature 6 - Sağ */}
        <FeatureRow 
          className="reverse"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <FeatureImage
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <img src={photo6} alt="Özellik 6" />
          </FeatureImage>
          <FeatureContent
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3>Grafik <span className="feature-highlight">Analizler</span></h3>
            <p>
              İnteraktif grafikler ve çizelgelerle verilerinizi görselleştirin. 
              Trend analizleri yapın ve iş kararlarınızı veriye dayalı alın.
            </p>
          </FeatureContent>
        </FeatureRow>
      </FeaturesSection>
    </div>
  );
};
